/**
 * desktopFrameWatch — is the live view's frame showing the workstation's page, or nothing? (2026-10-24)
 *
 * THE INCIDENT. Fernando's workstation answered its health endpoints with 200 and the page /desktop frames
 * (`/public/preston-realtime.html`) with an empty 403. The warm-up hold checks only the health endpoint, and the
 * old watchdog fired only when the frame's `load` never came, but a browser fires `load` for a 403, a 404, a 502
 * and its own error pages too. So the frame mounted an empty document and /desktop drew a blank gray rectangle
 * with no words, for everyone in the organization, for as long as it lasted.
 *
 * THE RULE. A frame is live only once the page in it has spoken: the page posts `requestBsAuth` from its first
 * script (preston-realtime.html, top level), which runs before the frame's `load`, and again once the shell's
 * hello pins its origin. So a frame that loaded and stayed silent for FRAME_PAGE_SPEAK_MS is not the page: the
 * shell shows the "not responding" card (retry, or open it in a new tab) instead of a blank. A frame that never
 * loads is caught as before, timed from when it MOUNTS (not from when its address is known: the warm-up hold
 * keeps the frame out of the page for up to 90 s, and a clock started earlier ended every long hold on "not
 * responding" without ever showing the frame).
 *
 * PURE: the hook (useDesktopSession.ts) owns the timers; this owns the verdict.
 */

/** How long a mounted frame may take to fire `load`. */
export const FRAME_LOAD_TIMEOUT_MS = 20_000;
/** How long a loaded frame's page may stay silent: a page that ran has spoken before its own `load`. */
export const FRAME_PAGE_SPEAK_MS = 15_000;

export interface FrameWatch {
  /** When the frame element was mounted (ms), or null while it is not in the page. */
  mountedAtMs: number | null;
  /** When it last fired `load`, or null. */
  loadedAtMs: number | null;
  /** Whether the page in it has posted anything from the workstation's origin. */
  spoke: boolean;
}

export type FrameVerdict =
  /** Not mounted, or still within its time. */
  | 'waiting'
  /** The page has spoken. */
  | 'live'
  /** Mounted, and `load` never came. */
  | 'stalled-no-load'
  /** Loaded, and the page never spoke: an error document, an empty response, a browser error page. */
  | 'stalled-silent';

export function emptyFrameWatch(): FrameWatch {
  return { mountedAtMs: null, loadedAtMs: null, spoke: false };
}

/** PURE. The frame's verdict at `nowMs`. */
export function frameVerdict(w: FrameWatch, nowMs: number): FrameVerdict {
  if (w.spoke) return 'live';
  if (w.mountedAtMs === null) return 'waiting';
  if (w.loadedAtMs === null) return nowMs - w.mountedAtMs >= FRAME_LOAD_TIMEOUT_MS ? 'stalled-no-load' : 'waiting';
  return nowMs - w.loadedAtMs >= FRAME_PAGE_SPEAK_MS ? 'stalled-silent' : 'waiting';
}

/** PURE. When to look again (ms from now), or null when nothing can change the verdict by itself. */
export function nextFrameCheckInMs(w: FrameWatch, nowMs: number): number | null {
  if (w.spoke || w.mountedAtMs === null) return null;
  const due = w.loadedAtMs === null ? w.mountedAtMs + FRAME_LOAD_TIMEOUT_MS : w.loadedAtMs + FRAME_PAGE_SPEAK_MS;
  return Math.max(0, due - nowMs);
}

export function isFrameStalled(v: FrameVerdict): boolean {
  return v === 'stalled-no-load' || v === 'stalled-silent';
}

/**
 * PURE. Whether the server's page probe (getWorkspaceBootStatus `pageStatus`) is a refusal the frame cannot get past:
 * a 4xx. No answer (0) or a 5xx can be a machine still starting, so those are left to the frame watch.
 */
export function isPageRefused(status: unknown): boolean {
  return typeof status === 'number' && status >= 400 && status < 500;
}
